import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchScrapeStats, fetchDiffStats, fetchDailyDiffStats, fetchTriageActions, fetchExtractionStats } from '@/utils/externalDiffsAPI';

function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function getCurrentWeekPeriod(): string {
  const now = new Date();
  const week = getISOWeek(now);
  return `${now.getFullYear()}W${week.toString().padStart(2, '0')}`;
}

function getPrevWeekPeriods(n: number): string[] {
  const periods: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i * 7);
    const w = getISOWeek(d);
    periods.push(`${d.getFullYear()}W${w.toString().padStart(2, '0')}`);
  }
  return periods;
}

function ProgressBar({ value, max, color = 'bg-blue-500' }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2">
      <div className={`${color} h-2 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function StatCard({ title, subtitle, children, loading, error }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  loading?: boolean;
  error?: Error | null;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {loading ? (
        <div className="space-y-3">
          <div className="h-8 bg-gray-100 rounded animate-pulse w-24" />
          <div className="h-2 bg-gray-100 rounded animate-pulse" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-32" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-500">Failed to load: {error.message}</p>
      ) : (
        children
      )}
    </div>
  );
}

function SegmentBar({ segments }: { segments: { label: string; value: number; color: string }[] }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  return (
    <div className="space-y-2">
      <div className="flex h-3 rounded-full overflow-hidden gap-px">
        {segments.map((seg) => {
          const pct = total > 0 ? (seg.value / total) * 100 : 0;
          return pct > 0 ? (
            <div
              key={seg.label}
              className={`${seg.color} h-3`}
              style={{ width: `${pct}%` }}
              title={`${seg.label}: ${seg.value.toLocaleString()}`}
            />
          ) : null;
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-sm ${seg.color}`} />
            <span className="text-xs text-gray-600">
              {seg.label}: <span className="font-medium text-gray-900">{seg.value.toLocaleString()}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const REFETCH_INTERVAL = 5 * 60 * 1000;


export default function Dashboard() {
  const [period, setPeriod] = useState(getCurrentWeekPeriod);
  const [tier, setTier] = useState<'all' | 'tier1' | 'tier2'>('all');
  const weekOptions = getPrevWeekPeriods(6);
  const currentWeek = getCurrentWeekPeriod();

  const { data: scrape, isLoading: scrapeLoading, error: scrapeError, dataUpdatedAt: scrapeAt } = useQuery({
    queryKey: ['dashboard-scrape-stats'],
    queryFn: fetchScrapeStats,
    staleTime: 2 * 60 * 1000,
    refetchInterval: REFETCH_INTERVAL,
  });

  const { data: diff, isLoading: diffLoading, error: diffError } = useQuery({
    queryKey: ['dashboard-diff-stats', period],
    queryFn: () => fetchDiffStats(period),
    staleTime: 2 * 60 * 1000,
    refetchInterval: REFETCH_INTERVAL,
  });

  const { data: triageMap = new Map() } = useQuery({
    queryKey: ['dashboard-triage-actions'],
    queryFn: fetchTriageActions,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: extraction, isLoading: extractionLoading, error: extractionError } = useQuery({
    queryKey: ['dashboard-extraction-stats'],
    queryFn: fetchExtractionStats,
    staleTime: 2 * 60 * 1000,
    refetchInterval: REFETCH_INTERVAL,
  });

  const DAILY_DAYS = 14;
  const { data: dailyDays, isLoading: dailyLoading, error: dailyError } = useQuery({
    queryKey: ['dashboard-daily-diff-stats', DAILY_DAYS],
    queryFn: () => fetchDailyDiffStats(DAILY_DAYS),
    staleTime: 5 * 60 * 1000,
    refetchInterval: REFETCH_INTERVAL,
  });
  const dailyRows = (dailyDays ?? []).map(d => {
    const [, m, day] = d.date.split('-');
    return {
      period: d.period,
      label: `${Number(m)}/${Number(day)}`,
      total: d.total,
      published: d.published,
      // event-bearing but not (yet) published — awaiting QA/publish
      pending: Math.max(0, d.with_events - d.published),
      noChange: Math.max(0, d.total - d.with_events),
    };
  });
  const dailyTotals = dailyRows.reduce((a, r) => ({ total: a.total + r.total, published: a.published + r.published, pending: a.pending + r.pending }), { total: 0, published: 0, pending: 0 });

  const scrapeCounts = useMemo(() => {
    const slugs = scrape?.outstanding_slugs ?? [];
    return slugs.reduce((acc, slug) => {
      const bucket = triageMap.get(slug)?.metadata?.bucket;
      if (bucket === 1) acc.scheduled++;
      else if (bucket === 2 || bucket === 3) acc.review++;
      else acc.notTriaged++;
      return acc;
    }, { scheduled: 0, review: 0, notTriaged: 0 });
  }, [scrape, triageMap]);

  const scrapeStats = tier === 'tier1' ? scrape?.tier1 : tier === 'tier2' ? scrape?.tier2 : scrape ? { processed_this_week: scrape.processed_this_week, outstanding_count: scrape.outstanding_count, total_active_pages: scrape.total_active_pages } : null;
  const diffStats = tier === 'tier1' ? diff?.tier1 : tier === 'tier2' ? diff?.tier2 : diff ? { complete_count: diff.complete_count, incomplete_count: diff.incomplete_count, no_scrapes_count: diff.no_scrapes_count } : null;

  const scrapePct = scrapeStats
    ? Math.round((scrapeStats.processed_this_week / scrapeStats.total_active_pages) * 100)
    : 0;

  const diffTotal = diffStats
    ? diffStats.complete_count + diffStats.incomplete_count + diffStats.no_scrapes_count
    : 0;

  const diffCoverage = diffStats && diffTotal > 0
    ? Math.round((diffStats.complete_count / diffTotal) * 100)
    : 0;

  const extractionDone = extraction ? extraction.extracted_count + extraction.fallback_count : 0;
  const extractionPct = extraction && extraction.total_active > 0
    ? Math.round((extractionDone / extraction.total_active) * 100)
    : 0;
  const extractionRemaining = extraction
    ? extraction.total_active - extractionDone - extraction.failed_count
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pipeline Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">
              Live progress · auto-refreshes every 5 min
              {scrape?.tier1 && (
                <span className="ml-2 text-xs text-blue-600 font-medium">
                  · {scrape.tier1.total_active_pages.toLocaleString()} watchlisted (Tier 1)
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              {(['all', 'tier1', 'tier2'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setTier(t)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    tier === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t === 'all' ? 'All' : t === 'tier1' ? 'Tier 1' : 'Tier 2'}
                </button>
              ))}
            </div>
            <label className="text-xs text-gray-500">Period:</label>
            <select
              value={period}
              onChange={e => setPeriod(e.target.value)}
              className="text-sm border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {weekOptions.map(w => (
                <option key={w} value={w}>
                  {w}{w === currentWeek ? ' (current)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Scrape Progress */}
          <StatCard
            title="Scrape Progress"
            loading={scrapeLoading}
            error={scrapeError as Error}
          >
            {scrapeStats && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    <span className={`text-3xl font-bold mr-1 ${scrapePct >= 90 ? 'text-green-600' : scrapePct >= 60 ? 'text-blue-600' : 'text-amber-600'}`}>
                      {scrapeStats.processed_this_week.toLocaleString()}
                    </span>
                    of {scrapeStats.total_active_pages.toLocaleString()} scraped ·{' '}
                    <Link to="/dashboard/scrapes" className="font-medium text-blue-600 hover:underline">
                      {scrapeStats.outstanding_count.toLocaleString()} outstanding
                    </Link>
                  </p>
                  <span className="text-2xl font-bold text-gray-400">{scrapePct}%</span>
                </div>
                <SegmentBar segments={[
                  { label: 'Scraped', value: scrapeStats.processed_this_week, color: 'bg-green-400' },
                  { label: 'Scheduled', value: scrapeCounts.scheduled, color: 'bg-blue-400' },
                  { label: 'Needs review', value: scrapeCounts.review, color: 'bg-amber-400' },
                  { label: 'Not triaged', value: scrapeCounts.notTriaged, color: 'bg-gray-300' },
                ]} />
                {scrapeAt > 0 && (
                  <p className="text-xs text-gray-400">Updated {new Date(scrapeAt).toLocaleTimeString()}</p>
                )}
              </div>
            )}
          </StatCard>

          {/* Diff Coverage */}
          <StatCard
            title="Diff Coverage"
            loading={diffLoading}
            error={diffError as Error}
          >
            {diffStats && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    <span className={`text-3xl font-bold mr-1 ${diffCoverage >= 90 ? 'text-green-600' : diffCoverage >= 60 ? 'text-blue-600' : 'text-amber-600'}`}>
                      {diffStats.complete_count.toLocaleString()}
                    </span>
                    of {diffTotal.toLocaleString()} complete · <span className="font-medium text-gray-700">{(diffStats.incomplete_count + diffStats.no_scrapes_count).toLocaleString()}</span> remaining
                  </p>
                  <span className="text-2xl font-bold text-gray-400">{diffCoverage}%</span>
                </div>
                <SegmentBar segments={[
                  { label: 'Complete', value: diffStats.complete_count, color: 'bg-green-400' },
                  { label: 'Incomplete', value: diffStats.incomplete_count, color: 'bg-amber-400' },
                  { label: 'No scrapes', value: diffStats.no_scrapes_count, color: 'bg-gray-300' },
                ]} />
              </div>
            )}
          </StatCard>

          {/* Daily Diffs (Hot 100) */}
          <StatCard
            title="Daily Diffs"
            subtitle="Hot 100 · one diff per company per UTC day · last 14 days"
            loading={dailyLoading}
            error={dailyError as Error}
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  <span className="text-3xl font-bold mr-1 text-green-600">{dailyTotals.published.toLocaleString()}</span>
                  published of {dailyTotals.total.toLocaleString()} daily diffs
                  {dailyTotals.pending > 0 && (
                    <> · <span className="font-medium text-amber-700">{dailyTotals.pending.toLocaleString()}</span> with changes awaiting publish</>
                  )}
                </p>
                <Link to={`/diffs2?period=${dailyRows[dailyRows.length - 1]?.period ?? ''}`} className="text-xs text-blue-600 hover:underline font-medium whitespace-nowrap">
                  View today &rarr;
                </Link>
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyRows} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barCategoryGap="25%">
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      cursor={{ fill: '#f3f4f6' }}
                      formatter={(value: number, name: string) => [value.toLocaleString(), name === 'published' ? 'Published' : name === 'pending' ? 'Changes, unpublished' : 'No pricing change']}
                      labelFormatter={(label: string, payload) => {
                        const p = payload && payload[0] && (payload[0].payload as { period: string; total: number });
                        return p ? `${label} · ${p.period} · ${p.total} diffs` : label;
                      }}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="published" stackId="d" fill="#4ade80" />
                    <Bar dataKey="pending" stackId="d" fill="#fbbf24" />
                    <Bar dataKey="noChange" stackId="d" fill="#d1d5db" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {[
                  { label: 'Published', color: 'bg-green-400' },
                  { label: 'Changes, unpublished', color: 'bg-amber-400' },
                  { label: 'No pricing change', color: 'bg-gray-300' },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <div className={`w-2.5 h-2.5 rounded-sm ${l.color}`} />
                    <span className="text-xs text-gray-600">{l.label}</span>
                  </div>
                ))}
                <span className="text-xs text-gray-400 ml-auto">A day with 0 bars = daily lane did not run</span>
              </div>
            </div>
          </StatCard>

          {/* Extraction Coverage */}
          <StatCard
            title="Extraction Coverage"
            subtitle="agent-browser-cloud · all active pages"
            loading={extractionLoading}
            error={extractionError as Error}
          >
            {extraction && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    <Link to="/dashboard/extractions" className="text-3xl font-bold mr-1 hover:underline" style={{ color: extractionPct >= 90 ? '#16a34a' : extractionPct >= 60 ? '#2563eb' : '#d97706' }}>
                      {extractionDone.toLocaleString()}
                    </Link>
                    of {extraction.total_active.toLocaleString()} extracted · <span className="font-medium text-gray-700">{extraction.failed_count.toLocaleString()}</span> failed
                  </p>
                  <Link
                    to="/dashboard/extractions"
                    className="text-xs text-blue-600 hover:underline font-medium whitespace-nowrap"
                  >
                    View details &rarr;
                  </Link>
                </div>
                <SegmentBar segments={[
                  { label: 'Extracted', value: extraction.extracted_count, color: 'bg-green-400' },
                  { label: 'Fallback', value: extraction.fallback_count, color: 'bg-amber-400' },
                  { label: 'Failed', value: extraction.failed_count, color: 'bg-red-400' },
                  { label: 'Remaining', value: extractionRemaining, color: 'bg-gray-300' },
                ]} />
                {extraction.watchlisted_total > 0 && (
                  <p className="text-xs text-gray-500">
                    Tier 1 (watchlisted): <span className="font-semibold text-gray-700">{extraction.watchlisted_extracted.toLocaleString()}</span> / {extraction.watchlisted_total.toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </StatCard>

        </div>
      </div>
    </div>
  );
}
